import { useState, useEffect, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useEventSalesStatus } from '@hooks/useEventSalesStatus'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { formatShortDate, formatTime, formatCurrency, formatNumber } from '@utils/formatters'
import { CITIES } from '@utils/geolocation'
import { copyToClipboard } from '@utils/clipboard'
import { Button, Badge, Loading, Modal, Notification } from '@components'
import { useAuth } from '@contexts/AuthContext'
import { useCityContext } from '@contexts/LocationContext'
import { useEventInteractions } from '@hooks/useEventInteractions'
import api from '@services/api'
import { MetaPixel } from '@services/metaPixel'
import { marked } from 'marked' // Import marked
import DOMPurify from 'dompurify' // Import DOMPurify
import { ENABLE_EVENT_INTERACTIONS } from '@config/features'
import './Event.css'

// Emojis disponíveis para reação
const AVAILABLE_EMOJIS = ['😍', '🤩', '😎', '🔥', '🎉', '💃', '🕺', '🫠', '🤐', '😜', '🥳', '❤️']

const Event = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    user,
    isAuthenticated,
    canInteract,
    canPurchaseTickets,
    isRegistrationComplete,
    isEmailVerified,
    hasTemporaryPassword
  } = useAuth()
  const { city } = useCityContext()

  // Query to load event data with 30s auto-update (pauses when tab is hidden, refetches immediately on focus)
  const { data: event = null, isLoading: loading } = useQuery({
    queryKey: ['event', id],
    queryFn: async () => {
      return await api.get(`/events/${id}`)
    },
    enabled: Boolean(id),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })

  const [showNotification, setShowNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState('')
  const [notificationType, setNotificationType] = useState('success') // 'success' or 'error'

  // Venda pendente
  const [showExistingSaleModal, setShowExistingSaleModal] = useState(false);
  const [existingSaleData, setExistingSaleData] = useState(null);

  // Hook para interações do evento
  const {
    interactions,
    loading: interactionsLoading,
    toggleEmoji,
    toggleRSVP
  } = useEventInteractions(id)

  // Estados para UI
  const [showAttendeesModal, setShowAttendeesModal] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  // Handlers de RSVP
  const handleRsvp = async (status) => {
    if (!isAuthenticated) {
      navigate(`/${city?.slug || ''}/login`, { state: { from: { pathname: `/${city?.slug || ''}/evento/${id}` } } })
      return
    }
    if (!canInteract) return

    try {
      await toggleRSVP(status)
    } catch (error) {
      console.error('Erro ao confirmar presença:', error)
    }
  }

  // Handlers de reações
  const handleReaction = async (emoji) => {
    if (!isAuthenticated) {
      navigate(`/${city?.slug || ''}/login`, { state: { from: { pathname: `/${city?.slug || ''}/evento/${id}` } } })
      return
    }
    if (!canInteract) return

    try {
      await toggleEmoji(emoji)
      setShowEmojiPicker(false)
    } catch (error) {
      console.error('Erro ao reagir:', error)
    }
  }

  // Calculate minPrice from ticket_types
  const minPrice = useMemo(() => {
    if (!event?.ticket_types || event.ticket_types.length === 0) return undefined;
    return Math.min(...event.ticket_types.map(ticket => ticket.value_cents)) / 100;
  }, [event]);

  // Effect for Meta Pixel tracking (PageView and ViewContent)
  useEffect(() => {
    if (event && event.meta_pixel_id) {
      const userData = {};
      if (user) {
        if (user.email) userData.email = user.email;
        if (user.phone) userData.phone = user.phone;
        if (user.name) userData.name = user.name;
      }
      
      MetaPixel.init(event.meta_pixel_id, userData);
      MetaPixel.track(event.meta_pixel_id, 'PageView');
      MetaPixel.track(event.meta_pixel_id, 'ViewContent', {
        content_name: event.name,
        content_category: event.event_type,
        content_ids: [event.event_id],
        content_type: 'product',
        value: minPrice,
        currency: 'BRL'
      });
    }
  }, [event, user, minPrice]);

  // Derive other event properties from the fetched event object
  const eventName = event?.name;
  const eventDescription = event?.description;
  const eventOrganizer = event?.organizer || 'ARQ Produções';
  const eventTotalStock = event?.total_stock;
  const eventSoldStock = event?.sold_stock;
  const eventTicketTypes = useMemo(() => {
    if (!event?.ticket_types) return [];
    return [...event.ticket_types].sort((a, b) => a.value_cents - b.value_cents);
  }, [event?.ticket_types]);
  const eventUnitId = event?.unit_id;

  // Logic to determine which date(s) to display
  const { displayStartDate, displayEndDate } = useMemo(() => {
    let startDate = null;
    let endDate = null;

    if (event?.date) {
      startDate = event.date; // Use date as both start and end for single day
      endDate = event.date;
    } else if (event?.start_date) {
      startDate = event.start_date;
      endDate = event?.end_date;
    }

    return {
      displayStartDate: startDate,
      displayEndDate: endDate
    };
  }, [event]);

  const { locationName, cityAddress } = useMemo(() => {
    const citySlug = eventUnitId ? Object.keys(CITIES).find(key => CITIES[key].unit_id === eventUnitId) : null;
    const defaultUnitName = citySlug ? `ARQZIN ${CITIES[citySlug].name}` : 'Local não informado';
    const defaultUnitAddress = citySlug ? (CITIES[citySlug].address || null) : null;

    return {
      locationName: event?.location_name || defaultUnitName,
      cityAddress: event?.address || defaultUnitAddress
    };
  }, [eventUnitId, event?.location_name, event?.address]);

  // Renderiza a descrição das regras do evento de Markdown para HTML
  const renderedRulesDescription = useMemo(() => {
    if (event?.rules_config?.description) {
      // marked.parse retorna uma string HTML
      const html = marked.parse(event.rules_config.description);
      // DOMPurify sanitiza o HTML para prevenir ataques XSS
      return DOMPurify.sanitize(html);
    }
    return null;
  }, [event?.rules_config?.description]);

  const handleBuyClick = async () => {
    if (!isAuthenticated) {
      navigate(`/${city?.slug || ''}/login`, { state: { from: { pathname: `/${city?.slug || ''}/evento/${id}` } } });
      return;
    }

    // Check if there is an active sale already
    try {
      const salesResponse = await api.get('/sales/?page=1&page_size=10');
      const activeSale = salesResponse?.items?.find(s => s.status === 'RESERVED');

      if (activeSale && activeSale.event_id !== id) {
        setExistingSaleData({
          saleId: activeSale.transaction_id,
          eventId: activeSale.event_id,
          eventName: activeSale.event?.name
        });
        setShowExistingSaleModal(true);
        return;
      }
    } catch (e) {
      console.error('Failed to check active sales:', e);
    }

    // Normal flow
    navigate(`/${city?.slug || ''}/checkout/${id}`);
  };

  const handleResumeExistingSale = () => {
    if (!existingSaleData) return;
    const { eventId: otherEventId } = existingSaleData;
    navigate(`/${city?.slug || ''}/checkout/${otherEventId}`);
    setShowExistingSaleModal(false);
  };

  const handleDiscardExistingSale = async () => {
    if (!existingSaleData) return;
    try {
      await api.delete(`/sales/${existingSaleData.saleId}`);
    } catch (error) {
      console.warn('Failed to cancel sale on API:', error);
    } finally {
      setExistingSaleData(null);
      setShowExistingSaleModal(false);
      // Now start the new one
      navigate(`/${city?.slug || ''}/checkout/${id}`);
    }
  };

  const isSoldOut = eventTotalStock !== undefined && eventSoldStock !== undefined && eventSoldStock >= eventTotalStock;

  const { isSalesNotStarted, isSalesEnded, isEventEnded: isEnded } = useEventSalesStatus(event);

  const salesOpeningFormatted = useMemo(() => {
    if (!event?.sales_start_at) return null;
    const shortDate = formatShortDate(event.sales_start_at);
    const time = formatTime(event.sales_start_at);
    return shortDate && time ? `${shortDate} às ${time}` : shortDate || time;
  }, [event?.sales_start_at]);

  const handleShowNotification = useCallback((message, type = 'success') => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000); // Hide after 3 seconds
  }, []);

  const handleCopyAddress = async () => {
    const addressToCopy = cityAddress || event?.address;
    if (addressToCopy) {
      const copied = await copyToClipboard(addressToCopy);
      if (copied) {
        handleShowNotification('Endereço copiado para a área de transferência!');
      } else {
        handleShowNotification('Falha ao copiar endereço.', 'error');
      }
    } else {
      handleShowNotification('Nenhum endereço disponível para copiar.', 'error');
    }
  };

  const handleOpenMap = () => {
    const addressToOpen = cityAddress || event?.address;
    if (addressToOpen) {
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressToOpen)}`;
      window.open(mapUrl, '_blank');
    } else {
      handleShowNotification('Nenhum endereço disponível para abrir no mapa.', 'error');
    }
  };

  // --- Valores Derivados do Hook de Interações ---

  const { rsvp, emojis } = interactions

  // Status de RSVP do usuário atual
  const rsvpStatus = useMemo(() => {
    if (!user || !rsvp) return null
    if (rsvp.done?.users.some(u => u.id === user.id)) return 'done'
    if (rsvp.interest?.users.some(u => u.id === user.id)) return 'interest'
    return null
  }, [user, rsvp])

  // Reação do usuário atual
  const userReaction = useMemo(() => {
    if (!user || !emojis) return null
    for (const emojiId in emojis) {
      if (emojis[emojiId].users.includes(user.id)) {
        return emojiId
      }
    }
    return null
  }, [user, emojis])

  const attendees = rsvp?.done?.users || []
  const totalAttendees = rsvp?.done?.count || 0
  const totalInterested = rsvp?.interest?.count || 0

  // Ordena reações por contagem
  const sortedReactions = useMemo(() =>
    Object.entries(emojis || {})
      .sort(([, a], [, b]) => b.count - a.count),
    [emojis]
  )

  const totalReactions = useMemo(() =>
    sortedReactions.reduce((sum, [, data]) => sum + data.count, 0),
    [sortedReactions]
  )


  if (loading) {
    return (
      <div className="event-page event-page--loading">
        <Loading />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="event-page event-page--not-found">
        <h1>Evento não encontrado</h1>
        <p>O evento que você está procurando não existe ou foi removido.</p>
        <Link to={`/${city?.slug || ''}`}>
          <Button variant="primary">Voltar para eventos</Button>
        </Link>
      </div>
    )
  }

  return (
    <main className="event-page">
      <div className="event-page__container container">
        <div className="event-page__layout">
          {/* Coluna Esquerda - Informações */}
          <div className="event-page__info">
            {/* Imagem do Evento no Mobile */}
            <div className="event-page__image-container event-page__image-container--mobile">
              <img
                src={event?.main_image_url || '/placeholder-event.jpg'}
                alt={eventName}
                className="event-page__image"
              />

              {/* Sales Opening Badge (Mobile) */}
              {!isEnded && isSalesNotStarted && (
                <div className="event-page__sales-badge">
                  <div className="event-page__sales-badge-icon">
                    <svg 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div className="event-page__sales-badge-content">
                    <span className="event-page__sales-badge-label">Abertura de vendas</span>
                    <span className="event-page__sales-badge-date">
                      {salesOpeningFormatted || 'Em breve'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Título e Data */}
            <div className="event-page__header">
              <h1 className="event-page__title">{eventName}</h1>
              {displayStartDate && (
                <p className="event-page__date">
                  {formatShortDate(displayStartDate)} às {formatTime(displayStartDate)}
                </p>
              )}
              <p className="event-page__location-name">{locationName}</p>

              {eventDescription && (
                <p className="event-page__short-desc">
                  {eventDescription.split('\n')[0]}
                </p>
              )}
            </div>

            {/* Infos Card */}
            <section className="event-page__infos">
              <h2 className="event-page__section-title">Infos</h2>

              <div className="event-page__info-card">
                <div className="event-page__info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <div className="event-page__info-content">
                  {displayStartDate && (
                    <>
                      <span className="event-page__info-label">
                        {formatShortDate(displayStartDate)}
                      </span>
                      <div className="event-page__info-detail">
                        Início: {formatShortDate(displayStartDate)}, às {formatTime(displayStartDate)}
                        {displayEndDate && (
                          <div className="event-page__sidebar-info-date-sub">
                            Término: {formatShortDate(displayEndDate)}, às {formatTime(displayEndDate)}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="event-page__info-card">
                <div className="event-page__info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="event-page__info-content">
                  <span className="event-page__info-label">{locationName}</span>
                  <span className="event-page__info-detail">{cityAddress || event?.address}</span>
                </div>
                <div className="event-page__info-actions">
                  <button
                    className="event-page__info-action"
                    title="Copiar endereço"
                    onClick={handleCopyAddress}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  </button>
                  <button
                    className="event-page__info-action"
                    title="Abrir no mapa"
                    onClick={handleOpenMap}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </button>
                </div>
              </div>
            </section>

            {!isEnded && !isSoldOut && !isSalesNotStarted && !isSalesEnded && (
              <div className="event-page__buy-button-container">
                {minPrice !== undefined && (
                  <p className="event-page__min-price">
                    A partir de {formatCurrency(minPrice)}
                  </p>
                )}

                {/* Mensagem de ação necessária */}
                {isAuthenticated && !canPurchaseTickets && (
                  <div className="event-page__buy-warning">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>
                      {!isRegistrationComplete ? (
                        <>Complete seu <Link to={`/${city?.slug || ''}/completar-cadastro`}>cadastro</Link> para comprar.</>
                      ) : !isEmailVerified ? (
                        <>Verifique seu e-mail para habilitar a compra.</>
                      ) : hasTemporaryPassword ? (
                        <>Altere sua senha temporária para continuar.</>
                      ) : (
                        <>Sua conta possui restrições para compra.</>
                      )}
                    </span>
                  </div>
                )}

                <Button
                  variant="primary"
                  fullWidth
                  size="lg"
                  onClick={handleBuyClick}
                  disabled={isAuthenticated && !canPurchaseTickets}
                >
                  Comprar Ingressos
                </Button>
              </div>
            )}
            {isSalesNotStarted && (
              <div className="event-page__buy-button-container">
                <div className="event-page__sales-not-started-banner">
                  <div className="event-page__sales-not-started-icon">
                    <svg 
                      width="18" 
                      height="18" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <span className="event-page__sales-not-started-text">
                    {event?.sales_start_at
                      ? `Vendas iniciam em ${formatShortDate(event.sales_start_at)} às ${formatTime(event.sales_start_at)}`
                      : 'Vendas ainda não iniciadas'}
                  </span>
                </div>
              </div>
            )}
            {isSalesEnded && (
              <div className="event-page__buy-button-container">
                <Button
                  variant="secondary"
                  fullWidth
                  size="lg"
                  disabled
                >
                  Vendas encerradas
                </Button>
              </div>
            )}

            {ENABLE_EVENT_INTERACTIONS && (
              <section className="event-page__rsvps">
                <h2 className="event-page__section-title">RSVPs</h2>

                <div className="event-page__rsvps-preview">
                  <div className="event-page__avatars-stack">
                    {attendees.slice(0, 4).map((attendee, index) => (
                      <div
                        key={attendee.id}
                        className="event-page__avatar"
                        style={{ zIndex: 4 - index }}
                      >
                        {attendee.image_thumbnail ? (
                          <img src={attendee.image_thumbnail} alt={attendee.name} />
                        ) : (
                          <span>{attendee.name.charAt(0)}</span>
                        )}
                      </div>
                    ))}
                    {totalAttendees > 4 && (
                      <div className="event-page__avatar event-page__avatar--more">
                        <span>+{formatNumber(totalAttendees - 4)}</span>
                      </div>
                    )}
                  </div>

                  <button
                    className="event-page__view-rsvps"
                    onClick={() => setShowAttendeesModal(true)}
                  >
                    Ver RSVPs
                  </button>
                </div>
              </section>
            )}

            {/* Organizador */}
            <section className="event-page__organizer-section">
              <div className="event-page__organizer-card">
                <span className="event-page__organizer-label">Produzido por:</span>
                <div className="event-page__organizer-info">
                  <div className="event-page__organizer-avatar">
                    <img src="/prod.png" alt="ARQ Produções" />
                  </div>
                  <span className="event-page__organizer-name">{eventOrganizer}</span>
                </div>
              </div>
            </section>
          </div>

          {/* Coluna Direita - Imagem e Compra */}
          <div className="event-page__sidebar">
            {/* Imagem do Evento */}
            <div className="event-page__image-container event-page__image-container--desktop">
              <img
                src={event?.main_image_url || '/placeholder-event.jpg'}
                alt={eventName}
                className="event-page__image"
              />
            </div>

            {ENABLE_EVENT_INTERACTIONS && (
              <div className="event-page__purchase-card">
                {/* Botões de RSVP */}
                <div className="event-page__rsvp-buttons">
                  <button
                    className={`event-page__rsvp-btn ${rsvpStatus === 'done' ? 'active' : ''}`}
                    onClick={() => handleRsvp('done')}
                    disabled={interactionsLoading}
                  >
                    Vou
                  </button>
                  <button
                    className={`event-page__rsvp-btn event-page__rsvp-btn--outline ${rsvpStatus === 'interest' ? 'active' : ''}`}
                    onClick={() => handleRsvp('interest')}
                    disabled={interactionsLoading}
                  >
                    Tenho interesse
                  </button>
                </div>

                {/* Reações */}
                <div className="event-page__reactions">
                  <div className="event-page__reactions-avatars">
                    {attendees.slice(0, 3).map((attendee, index) => (
                      <div
                        key={attendee.id}
                        className="event-page__reaction-avatar"
                        style={{ zIndex: 3 - index }}
                      >
                        {attendee.image_thumbnail ? (
                          <img src={attendee.image_thumbnail} alt="" />
                        ) : (
                          <span>{attendee.name.charAt(0)}</span>
                        )}
                      </div>
                    ))}
                    {totalReactions > 3 && (
                      <span className="event-page__reactions-count">
                        +{formatNumber(totalReactions - 3)}
                      </span>
                    )}
                  </div>

                  <div className="event-page__reactions-list">
                    {sortedReactions.map(([emoji, data]) => (
                      <button
                        key={emoji}
                        className={`event-page__reaction-item ${userReaction === emoji ? 'active' : ''}`}
                        onClick={() => handleReaction(emoji)}
                        title={`${data.count} pessoas reagiram`}
                        disabled={interactionsLoading}
                      >
                        <span className="event-page__reaction-emoji">{emoji}</span>
                        <span className="event-page__reaction-count">{data.count}</span>
                      </button>
                    ))}

                    {/* Botão de adicionar emoji */}
                    <div className="event-page__add-reaction">
                      <button
                        className="event-page__add-reaction-btn"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        disabled={interactionsLoading}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                          <line x1="9" y1="9" x2="9.01" y2="9" />
                          <line x1="15" y1="9" x2="15.01" y2="9" />
                        </svg>
                      </button>

                      {/* Emoji Picker */}
                      {showEmojiPicker && (
                        <div className="event-page__emoji-picker">
                          {AVAILABLE_EMOJIS.map(emoji => (
                            <button
                              key={emoji}
                              className={`event-page__emoji-option ${userReaction === emoji ? 'selected' : ''}`}
                              onClick={() => handleReaction(emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ingressos disponíveis (apenas display) */}
            {!isEnded && !isSoldOut && eventTicketTypes.length > 0 && (
              <div className="event-page__tickets-section">
                <h3 className="event-page__tickets-title">Tipos de Ingresso</h3>
                <div className="event-page__tickets-list">
                  {eventTicketTypes.map((ticketType) => (
                    <div key={ticketType.id} className="event-page__ticket">
                      <div className="event-page__ticket-info">
                        <h4 className="event-page__ticket-name">{ticketType.name}</h4>
                        <p className="event-page__ticket-description">{ticketType.description}</p>
                        <div className="event-page__ticket-meta">
                          <span className="event-page__ticket-price">
                            {formatCurrency(ticketType.value_cents / 100)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* New button at the bottom of the ticket types section */}
                {isSalesNotStarted && (
                  <div className="event-page__sales-not-started-alert">
                    <div className="event-page__sales-not-started-icon">
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <span className="event-page__sales-not-started-message">
                      {event?.sales_start_at
                        ? `Vendas iniciam em ${formatShortDate(event.sales_start_at)} às ${formatTime(event.sales_start_at)}`
                        : 'As vendas para este evento ainda não foram iniciadas.'}
                    </span>
                  </div>
                )}
                {isSalesEnded && (
                  <p className="event-page__sales-not-started-message">
                    Vendas encerradas.
                  </p>
                )}

                {/* Mensagem de ação necessária */}
                {isAuthenticated && !canPurchaseTickets && (
                  <div className="event-page__buy-warning">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>
                      {!isRegistrationComplete ? (
                        <>Complete seu <Link to={`/${city?.slug || ''}/completar-cadastro`}>cadastro</Link> para comprar.</>
                      ) : !isEmailVerified ? (
                        <>Verifique seu e-mail para habilitar a compra.</>
                      ) : hasTemporaryPassword ? (
                        <>Altere sua senha temporária para continuar.</>
                      ) : (
                        <>Sua conta possui restrições para compra.</>
                      )}
                    </span>
                  </div>
                )}

                <Button
                  variant="primary"
                  fullWidth
                  size="lg"
                  onClick={handleBuyClick}
                  className="event-page__buy-button"
                  disabled={(isSalesNotStarted || isSalesEnded) || (isAuthenticated && !canPurchaseTickets)}
                >
                  Comprar Ingressos
                </Button>
              </div>
            )}                                                                              {/* Badges de status */}
            {(isEnded || isSoldOut) && (
              <div className="event-page__status-badge">
                {isEnded && <Badge variant="secondary" size="lg">Evento encerrado</Badge>}
                {!isEnded && isSoldOut && <Badge variant="danger" size="lg">Ingressos esgotados</Badge>}
              </div>
            )}
          </div>
 
          {/* Descrição completa - agora usando rules_config.description */}
          {renderedRulesDescription && (
            <section className="event-page__description">
              <h2 className="event-page__section-title">Política de Ingressos</h2>
              <div
                className="event-page__description-text event-page__description-scrollable"
                dangerouslySetInnerHTML={{ __html: renderedRulesDescription }}
              />
            </section>
          )}
        </div>
      </div>
      {/* Modal de Participantes */}
      {ENABLE_EVENT_INTERACTIONS && (
        <Modal
          isOpen={showAttendeesModal}
          onClose={() => setShowAttendeesModal(false)}
          title="Quem vai"
          size="md"
        >
          <div className="event-page__attendees-modal">
            <div className="event-page__attendees-tabs">
              <button className="event-page__attendees-tab active">
                Confirmados ({formatNumber(totalAttendees)})
              </button>
              <button className="event-page__attendees-tab">
                Interessados ({formatNumber(totalInterested)})
              </button>
            </div>

            <div className="event-page__attendees-list">
              {attendees.map(attendee => (
                <div key={attendee.id} className="event-page__attendee-item">
                  <div className="event-page__attendee-avatar">
                    {attendee.image_thumbnail ? (
                      <img src={attendee.image_thumbnail} alt={attendee.name} />
                    ) : (
                      <span>{attendee.name.charAt(0)}</span>
                    )}
                  </div>
                  <span className="event-page__attendee-name">{attendee.name}</span>
                </div>
              ))}
            </div>

            {totalAttendees > attendees.length && (
              <button className="event-page__load-more">
                Carregar mais
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* Modal de Venda Existente */}
      <Modal
        isOpen={showExistingSaleModal}
        onClose={() => setShowExistingSaleModal(false)}
        title="Venda Pendente"
        size="md"
      >
        <div className="checkout-page__existing-sale-modal">
          <div className="checkout-page__info-box checkout-page__info-box--warning">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <div>
              <strong>Atenção:</strong> Você já possui uma transação em andamento para outro evento.
            </div>
          </div>

          <p className="checkout-page__modal-text">
            Não é possível realizar duas compras simultaneamente. O que deseja fazer?
          </p>

          <div className="checkout-page__modal-actions">
            <Button
              variant="secondary"
              fullWidth
              onClick={handleResumeExistingSale}
            >
              Voltar para a transação anterior
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={handleDiscardExistingSale}
            >
              Cancelar anterior e começar nova
            </Button>
          </div>
        </div>
      </Modal>

      {showNotification && (
        <Notification message={notificationMessage} type={notificationType} onClose={() => setShowNotification(false)} />
      )}
    </main>
  )
}

export default Event
